import DOMPurify from "dompurify";

export function hashToArray<T> (hash: IIdDictionary<T>): T[] {
    let keys = Object.keys(hash);
    return keys.map(key => hash[key]) || [];
}

export function findInHash<T> (hash: IIdDictionary<T>, property: string, value): T | null {
    let record;

    Object.keys(hash).forEach(key => {
        if (hash[key][property] === value) {
            record = hash[key];
        }
    });

    return record || null;
}

export function closest (elem: HTMLElement, selector: string): HTMLElement | null {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    let matchesSelector = elem && (elem.matches || elem.webkitMatchesSelector || elem["mozMatchesSelector"] || elem["msMatchesSelector"]);
    let element = elem;
    if (matchesSelector) {
        while (element && element !== document.body) {
            if (matchesSelector.bind(element)(selector)) {
                return element;
            } else {
                element = <HTMLElement>element.parentNode;
            }
        }
    }
    return null;
}

export function htmlEscape(str: string): string {
    return String(str || "")
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
        .replace(/`/g, "&#96;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

export function htmlUnescape(str: string): string {
    return String(str || "")
        .replace(/&&amp;/g, "\&")
        .replace(/"&quot;/g, "\"")
        .replace(/&#39;/g, "\'")
        .replace(/&#96;/g, "\`")
        .replace(/&lt;/g, "\<")
        .replace(/&gt;/g, "\>");
}

export function sanitizeHtml(html: string | null) {
    if (!html) {
        return "";
    }

    return DOMPurify.sanitize(html);
}

export function isEqual(a, b, ignoredObjectKeys: string[] = []) {
    if (a === b) {
        return true;
    }

    let className = Object.prototype.toString.call(a);
    if (className !== Object.prototype.toString.call(b)) {
        return false;
    }

    // checking values which where created via constructor function
    switch (className) {
        case "[object RegExp]":
        case "[object String]":
            return `${ a }` === `${ b }`;
        case "[object Number]":
            // checking is NaN
            if (+a !== +a) {
                return +b !== +b;
            } else {
                return +a === +b;
            }
        case "[object Date]":
        case "[object Boolean]":
          return +a === +b;
        default:
            break;
    }

    let areArrays = className === "[object Array]";
    if (!areArrays) {
        if (typeof a !== "object" || typeof b !== "object") {
            return false;
        }

        if (a.constructor !== b.constructor) {
            return false;
        }

        let keysA = Object.keys(a);
        let keysB = Object.keys(b);

        let lengthA = keysA.length;

        if (keysB.length !== lengthA) {
            return false;
        }

        let usedKeys = {};
        while (lengthA--) {
            let keyA = keysA[lengthA];
            let keyB = keysB[lengthA];

            if (!usedKeys[keyA] && ignoredObjectKeys.indexOf(keyA) === -1 && !isEqual(a[keyA], b[keyA])) {
                return false;
            }
            // Ensure we check the other key in case ordering of properties is incorrect
            usedKeys[keyA] = keyA;
            if (!usedKeys[keyB] && ignoredObjectKeys.indexOf(keyB) === -1 && !isEqual(a[keyB], b[keyB])) {
                return false;
            }
            usedKeys[keyB] = keyB;
        }
    } else {
        let length = a.length;

        if (length !== b.length) {
            return false;
        }

        while (length--) {
            if (!isEqual(a[length], b[length], ignoredObjectKeys)) {
                return false;
            }
        }
    }

    return true;
}

function changeDisplay(element: HTMLElement, newValue: string) {
    element.style.display = newValue;
    return element;
}

function getRealDisplay(elem) {
    if (elem.currentStyle) {
        return elem.currentStyle.display;
    } else if (window.getComputedStyle) {
        let computedStyle = window.getComputedStyle(elem);
        return computedStyle.getPropertyValue("display");
    }
    return "none";
}

export function isVisible (el: HTMLElement) {
    return getRealDisplay(el) !== "none";
}

export function show(el: HTMLElement, displayType = "block") {
    if (getRealDisplay(el) !== "none") {
        return el;
    }

    changeDisplay(el, "");

    if (getRealDisplay(el) === "none") {
        let body = document.body;
        let nodeName = el.nodeName;

        let testElem = document.createElement(nodeName);
        body.appendChild(testElem);
        let display = getRealDisplay(testElem);

        if (display === "none" || displayType) {
            display = displayType;
        }

        body.removeChild(testElem);

        changeDisplay(el, display);
    }

    return el;
}

export function hide (element: HTMLElement) {
    return changeDisplay(element, "none");
}

export function toggle (el: HTMLElement, toggleState?: boolean) {
    return toggleState === false || (typeof toggleState !== "boolean" && isVisible(el)) ? hide(el) : show(el);
}

export function empty (container: HTMLElement) {
    let child = container.lastChild;
    while (child) {
        container.removeChild(child);
        child = container.lastChild;
    }
}

export function removeElement (element: HTMLElement | null) {
    if (element) {
        let parent = element.parentNode;
        parent && parent.removeChild(element);
    }
}

export function toggleDisable (element: HTMLElement | null, disable = true) {
    if (element) {
        if (disable) {
            element.setAttribute("disabled", "disabled");
        } else {
            element.removeAttribute("disabled");
        }
    }
}

export function createElement<K extends keyof HTMLElementTagNameMap> (tagName: K, className?: string, id?: string): HTMLElementTagNameMap[K] {
    let newElement = document.createElement(tagName);
    className && newElement.setAttribute("class", className);
    id && newElement.setAttribute("id", id);
    return newElement;
}

export function setCss (element?: HTMLElement, cssObj?) {
    if (element) {
        Object.keys(cssObj).forEach(prop => {
            element.style[prop] = cssObj[prop];
        });
    }
}

export let echo = a => a;

export let noOp = () => {};

interface IExtend {
    <T1, T2>(isDeepClone: boolean, target: T1, source: T2): T1 & T2;
    <T1, T2, T3>(isDeepClone: boolean, target: T1, source1: T2, source2: T3): T1 & T2 & T3;
    <T1, T2, T3, T4>(isDeepClone: boolean, target: T1, source1: T2, source2: T3, source3: T4): T1 & T2 & T3 & T4;
    <T1, T2, T3, T4, T5>(isDeepClone: boolean, target: T1, source1: T2, source2: T3, source3: T4, source4: T5): T1 & T2 & T3 & T4 & T5;
    (isDeepClone: boolean, target: any, ...source: any[]): any;
    <T1, T2>(target: T1, source: T2): T1 & T2;
    <T1, T2, T3>(target: T1, source1: T2, source2: T3): T1 & T2 & T3;
    <T1, T2, T3, T4>(target: T1, source1: T2, source2: T3, source3: T4): T1 & T2 & T3 & T4;
    <T1, T2, T3, T4, T5>(target: T1, source1: T2, source2: T3, source3: T4, source4: T5): T1 & T2 & T3 & T4 & T5;
    (target: any, ...source: any[]): any;
}

let isArray = arr => Array.isArray(arr);
let isUsualObject = obj => Object.prototype.toString.call(obj).indexOf("Object") !== -1;

export let extend: IExtend = function (...rest) {
    let length = rest.length;
    let fullCopy = false;
    let res = rest[0];
    let i = 1;

    if (typeof res === "boolean") {
        fullCopy = res;
        res = rest[1];
        i++;
    }

    while (i !== length) {
        let src = rest[i];
        let srcKeys = Object.keys(src);

        for (let j = 0; j < srcKeys.length; j++) {
            let srcAttr = src[srcKeys[j]];

            if (fullCopy && (isUsualObject(srcAttr) || isArray(srcAttr))) {
                let resAttr = res[srcKeys[j]];

                resAttr = res[srcKeys[j]] = (isUsualObject(resAttr) || isArray(resAttr)) ? resAttr : (isArray(srcAttr) ? [] : {});
                extend(fullCopy, resAttr, srcAttr);
            } else {
                res[srcKeys[j]] = src[srcKeys[j]];
            }
        }
        i++;
    }
    return res;
};

export function getObjectCopy<T = any>(obj: any) {
    return (extend({}, obj)) as T;
}

export function getArrayCopy<T = any>(arr: any) {
    return (extend([], arr)) as T[];
}

export function jsonClone<T = any> (entity) {
    return JSON.parse(JSON.stringify(entity)) as T;
}

export function isEmpty (entity): boolean {
    if (entity) {
        if (isArray(entity) || typeof (entity) === "string") {
            return entity.length === 0;
        }

        if (typeof (entity) === "object") {
            return Object.keys(entity).length === 0;
        }

        return false;
    }

    return true;
}

/**
 * Finds all items in the dictionary that match search criteria
 * @param dict Id indexed dictionary
 * @param searchObj Search object that could contain an id
 */
export function filterHash<T extends { id: string }>(dict: IIdDictionary<T>, searchObj?: { id?: string }): T[] {
    return Object.keys(dict).map(k => dict[k]).filter(item => {
        if (!searchObj) { return true; }

        if (searchObj.id) { return searchObj.id === item.id; }

        for (let key in searchObj) {
            if (searchObj[key] !== item[key]) {
                return false;
            }
        }
        return true;
    });
}

/**
 * Finds the first item that matches the search
 */
export function find<T>(items: T[], searchCallback?: Function): T | null {
    let retVal: T | null = null;

    items.some(item => {
        if (searchCallback && searchCallback(item)) {
            retVal = item;
            return true;
        }
        return false;
    });

    return retVal;
}

/**
 * Find the first item in the dictionary that match search criteria
 * @param dict Id indexed dictionary
 * @param search Search object that could contain an id
 */
export function findHash<T>(dict: IIdDictionary<T>, searchCallback?: Function): T | null {
    return find(Object.keys(dict).map(k => dict[k]), searchCallback);
}